import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Flex, Icon, Text } from '@kognitos/lattice';

import { getAssetFileResourceName } from '@/modules/library/utils';
import type { TResolvedFile } from '@/modules/outputs/types';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { decodeBishopFilename } from '@/shared/utils/file-utils';

import { DraftRailSection, RailGroupLabel } from './DraftRailSection';
import { LIBRARY_ROW_CLASS, normalizeCollectionSlug } from './rail-utils';
import type { IRailLibrary } from './use-rail-library';

interface IRailLibrarySectionProps {
  library: IRailLibrary;
  /** Owned by the rail, so a preview survives selecting a step. */
  onPreviewFile: (file: TResolvedFile) => void;
}

/** The rail's Library block: the assets and collections the steps reach. */
export function RailLibrarySection({
  library,
  onPreviewFile,
}: IRailLibrarySectionProps) {
  const { t: tAutomation } = useTranslation('automation');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { assetUris, collectionSlugs, collectionBySlug } = library;

  return (
    <DraftRailSection
      title={tAutomation('automation.draftRail.library')}
      name="library"
      count={assetUris.length + collectionSlugs.length}
    >
      {assetUris.length === 0 && collectionSlugs.length === 0 && (
        <Text level="xSmall" color="muted" className="px-1 py-2">
          {tAutomation('automation.draftRail.noLibraryResources')}
        </Text>
      )}
      {/* Each subtitle is gated on its own group: an empty group leaves
       * no heading stranded over rows that aren't there. */}
      {assetUris.length > 0 && (
        <RailGroupLabel>
          {tAutomation('automation.draftRail.libraryAssets')}
        </RailGroupLabel>
      )}
      {assetUris.map(uri => {
        const fileUrl = getAssetFileResourceName(
          { reference_uri: uri },
          { organization: organizationId, workspace: workspaceId },
        );
        const label = decodeBishopFilename(uri);

        return (
          <Button
            key={uri}
            type="button"
            variant="ghost"
            disabled={!fileUrl}
            className={LIBRARY_ROW_CLASS}
            onClick={() =>
              fileUrl && onPreviewFile({ type: 'file-remote', fileUrl })
            }
            data-testid={`draft-rail-asset-${uri}`}
          >
            <Icon type="FileText" size="sm" className="shrink-0" />
            <Text level="xSmall" className="min-w-0 truncate">
              {label}
            </Text>
          </Button>
        );
      })}
      {collectionSlugs.length > 0 && (
        <RailGroupLabel>
          {tAutomation('automation.draftRail.libraryCollections')}
        </RailGroupLabel>
      )}
      {collectionSlugs.map(slug => {
        const collection = collectionBySlug.get(normalizeCollectionSlug(slug));

        if (!collection) {
          return (
            <Flex
              key={slug}
              align="center"
              gap={2}
              className="min-w-0 rounded-xl bg-layout px-3 py-2"
              data-testid={`draft-rail-collection-${slug}`}
            >
              <Icon type="Database" size="sm" className="shrink-0" />
              <Text level="xSmall" className="min-w-0 truncate">
                {slug}
              </Text>
            </Flex>
          );
        }

        return (
          <Button
            key={slug}
            asChild
            variant="ghost"
            className={LIBRARY_ROW_CLASS}
            data-testid={`draft-rail-collection-${slug}`}
          >
            {/* A collection opens in a new tab so the draft, and any
             * unsaved chat in it, survives the trip. */}
            <Link
              to={getRoutePath('LIBRARY_COLLECTION', {
                orgId: organizationId,
                workspaceId,
                collectionId: collection.id,
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon type="Database" size="sm" className="shrink-0" />
              <Text level="xSmall" className="min-w-0 flex-1 truncate">
                {collection.displayName}
              </Text>
              <Icon
                type="ArrowUpRight"
                size="xs"
                className="shrink-0 text-muted-foreground"
              />
            </Link>
          </Button>
        );
      })}
    </DraftRailSection>
  );
}
